import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Grid, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Product from '../components/Product';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const Collection = () => {
  const { user, api } = useAuth();
  const { collectionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const observer = useRef();
  const loadingRef = useRef(null);

  // Set categories based on user gender
  useEffect(() => {
    if (user?.gender) {
      if (user.gender.toLowerCase() === 'women') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'dresses', name: 'Dresses' },
          { id: 'tops', name: 'Tops' },
          { id: 'pants', name: 'Pants' },
          { id: 'skirts', name: 'Skirts' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'bags', name: 'Bags' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      } else if (user.gender.toLowerCase() === 'men') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'tops', name: 'Tops' },
          { id: 'pants', name: 'Pants' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'suits', name: 'Suits' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'underwear', name: 'Underwear' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      }
    }
  }, [user?.gender]);

  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setPage(1);
    setDisplayedProducts([]);
    setHasMore(true);
  };

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/api/collections/${collectionId}/products`, {
          params: {
            page,
            category: activeCategory,
            limit: 20
          }
        });

        const { products, total } = response.data;
        
        setDisplayedProducts(prevProducts => {
          if (page === 1) return products;
          return [...prevProducts, ...products];
        });

        setHasMore(displayedProducts.length < total);
      } catch (err) {
        console.error('Error fetching collection:', err);
        setError('Failed to load collection. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchCollection();
    }
  }, [user, api, collectionId, page, activeCategory]);

  const getCollectionEmoji = (title) => {
    const emojiMap = {
      'City Explorer': '🌆',
      'Night Out': '🌙',
      'Active Life': '🏃',
      'Brunch & Beyond': '🍳',
      'Coffee Culture': '☕️'
    };
    
    return emojiMap[title] || '👗';
  };

  if (loading && page === 1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto' }}>
        <Typography color="error" sx={{ textAlign: 'center' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        mt: { xs: 7, sm: 8 },
        mb: { xs: 9, sm: 10 },
        px: 3, 
        maxWidth: 600, 
        mx: 'auto', 
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1,
        height: '100%',
        overflow: 'auto',
        pt: 2,
        pb: 2,
      }}
    >
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {getCollectionTitle(collectionId)} {getCollectionEmoji(getCollectionTitle(collectionId))}
      </Typography>

      {/* Category Carousel - Make it sticky */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: { xs: 56, sm: 64 }, // Adjust based on navbar height
          zIndex: 2,
          mb: 3, 
          pb: 1,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {categories.map((category) => (
          <Button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            sx={{
              mr: 2,
              px: 2,
              py: 1,
              color: activeCategory === category.id ? 'black' : theme.palette.grey[400],
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '10px',
              position: 'relative',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                borderRadius: '10px',
                height: 3,
                backgroundColor: activeCategory === category.id ? 'primary.main' : 'transparent',
                transition: 'width 0.2s ease',
                width: activeCategory === category.id ? `${category.name.length * 8}px` : 0,
              }
            }}
          >
            {category.name}
          </Button>
        ))}
      </Box>

      <Grid 
        container 
        spacing={2}
        sx={{
          // Add margin to account for sticky header
          mt: 1,
          // Ensure consistent spacing
          mx: -1,
          width: 'calc(100% + 16px)',
        }}
      >
        {displayedProducts.map((product, index) => (
          <Grid 
            item 
            xs={6} 
            sm={6} 
            md={6} 
            key={`${product.id}-${index}`}
            ref={index === displayedProducts.length - 1 ? lastProductElementRef : null}
            sx={{
              // Add padding to ensure consistent spacing
              p: 1,
              // Ensure each grid item maintains its size
              minHeight: { xs: 280, sm: 320 },
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Product 
                id={product.id}
                image={product.media?.images?.find(img => img.isPrimary)?.url || product.media?.images?.[0]?.url}
                brand={product.brand?.name}
                name={product.title}
                price={product.price}
                originalPrice={product.original_price}
                discountPercentage={product.discount_percentage}
                productDetails={product.product_details}
                variants={product.variants}
                category={product.category}
                color={product.variants?.[0]?.colour}
                product={product}
                sx={{
                  height: '100%',
                  // Add consistent spacing between cards
                  mb: 2,
                  // Ensure proper aspect ratio
                  '& .MuiCardMedia-root': {
                    paddingTop: '133.33%', // 4:3 aspect ratio
                    backgroundSize: 'cover',
                  },
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {displayedProducts.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          No products found in this collection
        </Typography>
      )}

      {loading && page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} ref={loadingRef}>
          <CircularProgress size={30} />
        </Box>
      )}

      {!hasMore && displayedProducts.length > 0 && (
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            mt: 4, 
            color: 'text.secondary',
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 2
          }}
        >
          You've reached the end of the list
        </Typography>
      )}
    </Box>
  );
};

// Helper function to get collection title
const getCollectionTitle = (id) => {
  const titles = {
    'city-explorer': 'City Explorer',
    'night-out': 'Night Out',
    'active-life': 'Active Life',
    'brunch-beyond': 'Brunch & Beyond',
    'coffee-culture': 'Coffee Culture'
  };
  return titles[id] || 'Collection';
};

export default Collection; 